import { ReactNode } from 'react';
import Grid from '@material-ui/core/Grid';
import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
  catalogPlugin,
  catalogTranslationRef,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogFilterLayout,
  EntityListProvider,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';

// `CatalogFilterLayout.Filters` só recolhe os filtros num drawer abaixo do
// breakpoint informado (default `md`). No Material-UI v4,
// `theme.breakpoints.down('xl')` equivale a `up('xs')` ("xl down applies to
// all sizes"), então o painel fica sempre recolhido, em qualquer largura.
// Se o Backstage migrar esse componente para MUI v5, onde `down('xl')` passa a
// ser `max-width: 1536px`, trocar por um número grande (ex.: 100000).
const ALWAYS_DRAWER_OPTIONS = { drawerBreakpoint: 'xl' as const };

export interface CatalogPageProps {
  columns?: TableColumn<CatalogTableRow>[] | CatalogTableColumnsFunc;
  tableOptions?: TableProps<CatalogTableRow>['options'];
  filters: ReactNode;
}

const CatalogHeaderActions = () => {
  const { t } = useTranslationRef(catalogTranslationRef);
  const createComponentLink = useRouteRef(
    catalogPlugin.externalRoutes.createComponent,
  );
  const { allowed } = usePermission({
    permission: catalogEntityCreatePermission,
  });

  return (
    <>
      {allowed && (
        <CreateButton
          title={t('indexPage.createButtonTitle')}
          to={createComponentLink?.()}
        />
      )}
      <SupportButton>{t('indexPage.supportButtonContent')}</SupportButton>
    </>
  );
};

/**
 * Página de índice do catálogo. Reproduz a `BaseCatalogPage` do
 * `@backstage/plugin-catalog` (não exportada) com duas diferenças: os filtros
 * ficam sempre recolhidos num drawer e a tabela ocupa toda a largura.
 */
export const CatalogPage = ({
  columns,
  tableOptions,
  filters,
}: CatalogPageProps) => {
  const { t } = useTranslationRef(catalogTranslationRef);
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';

  return (
    <EntityListProvider>
      <PageWithHeader title={t('indexPage.title', { orgName })} themeId="home">
        <Content>
          <ContentHeader title="">
            <CatalogHeaderActions />
          </ContentHeader>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters options={ALWAYS_DRAWER_OPTIONS}>
              {filters}
            </CatalogFilterLayout.Filters>
            <Grid item xs={12}>
              <CatalogTable columns={columns} tableOptions={tableOptions} />
            </Grid>
          </CatalogFilterLayout>
        </Content>
      </PageWithHeader>
    </EntityListProvider>
  );
};
